import api, { IPaginationResponse } from 'config/axios';
import AppService from 'common/app-service';
import {
  ICarrierCoverageStatus,
  IGuaranteeSettings,
  IGuaranteeSettingsTakerLimit,
  IGuaranteeSettingsSummary,
  IGuaranteeSettingsSummaryProjectionPoint,
} from 'common/guarantee.interface';

export interface IUpdateGuaranteeSettingsPayload {
  enabled?: boolean;
  protectedExposure?: number;
  coverageIndex?: number;
  cashCollateral?: number;
  maxTermDays?: number;
  defaultExposure?: number;
  isDefaulted?: boolean;
}

export default class GuaranteeService extends AppService {
  constructor() {
    super('guarantee', api);
  }

  async listSettings(params?: {
    financierId?: number;
    carrierRootId?: number;
    page?: number;
    limit?: number;
  }): Promise<IPaginationResponse<IGuaranteeSettings>> {
    const { data } = await this.api.get(`${this.root()}/settings`, { params });
    return data;
  }

  async updateSettings(id: number, payload: IUpdateGuaranteeSettingsPayload): Promise<IGuaranteeSettings> {
    const { data } = await this.api.put(`${this.root()}/settings/${id}`, payload);
    return data;
  }

  async createTakerLimit(
    settingsId: number,
    payload: { takerRootId: number; concentrationLimit: number | null }
  ): Promise<IGuaranteeSettingsTakerLimit> {
    const { data } = await this.api.post(`${this.root()}/settings/${settingsId}/taker-limits`, payload);
    return data;
  }

  async updateTakerLimit(
    limitId: number,
    payload: { takerRootId?: number; concentrationLimit?: number | null }
  ): Promise<IGuaranteeSettingsTakerLimit> {
    const { data } = await this.api.put(`${this.root()}/settings/taker-limits/${limitId}`, payload);
    return data;
  }

  async deleteTakerLimit(limitId: number): Promise<void> {
    await this.api.delete(`${this.root()}/settings/taker-limits/${limitId}`);
  }

  async getSummary(id: number): Promise<IGuaranteeSettingsSummary> {
    const { data } = await this.api.get(`${this.root()}/summary/${id}`);
    return data;
  }

  async getSummaryProjection(id: number): Promise<IGuaranteeSettingsSummaryProjectionPoint[]> {
    const { data } = await this.api.get(`${this.root()}/summary/${id}/projection`);
    return data;
  }

  async getSettings(): Promise<IGuaranteeSettings | null> {
    const response = await this.listSettings({ page: 1, limit: 1 });
    return response.items?.[0] ?? null;
  }

  async getCoverageStatus(carrierRootIds: number[]): Promise<ICarrierCoverageStatus[]> {
    if (!carrierRootIds.length) return [];
    const { data } = await this.api.get(`${this.root()}/summary/coverage-status`, {
      params: { carrierRootIds },
    });
    return data;
  }
}
